'use client';

import { useState, useMemo, type CSSProperties } from 'react';
import { useI18n } from '@/lib/i18n';
import { compressImage } from '@/lib/imageCompress';
import type { InventoryItem, NeedleKind } from '@/lib/types';
import { inferCableLengthFromNotes } from '@/lib/needleAvailability';
import BlobImage from '@/components/BlobImage';
import { Card } from '@/components/ui/Card';
import { ItalicBody } from '@/components/ui/ItalicBody';
import { ZineButton } from '@/components/ui/ZineButton';

/** The four inventory item categories — equal to InventoryItem['type'].
 *  Shared by the inventory page (tab state) and these form components. */
export type TabType = 'needle' | 'cable' | 'hook' | 'accessory';

export function InventoryForm({ type, initial, onSubmit, onCancel, onDelete }: {
  type: TabType;
  initial?: InventoryItem;
  onSubmit: (data: Omit<InventoryItem, 'id' | 'createdAt'>) => void;
  onCancel: () => void;
  /** Edit-mode only. When provided, the form renders a small,
   *  visually-de-emphasised trash icon next to Cancel/Save and
   *  triggers `onDelete` after a `confirm()` dialog. The two-step
   *  (tap trash → confirm) is the "hard to accidentally delete"
   *  guard Veronika asked for (msg 2357, 2026-05-06). */
  onDelete?: () => Promise<void> | void;
}) {
  const { t } = useI18n();
  const [name, setName] = useState(initial?.name || '');
  const [brand, setBrand] = useState(initial?.brand || '');
  const [size, setSize] = useState(initial?.size || '');
  const [length, setLength] = useState(initial?.length || '');
  const [material, setMaterial] = useState(initial?.material || '');
  const [quantity, setQuantity] = useState<string>(String(initial?.quantity ?? 1));
  const [description, setDescription] = useState(
    type === 'accessory' ? (initial?.notes || '') : ''
  );
  const [photo, setPhoto] = useState<string | undefined>(initial?.photo);

  const handlePhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // 400x400 matches the yarn photo compression — plenty for a small
    // list thumbnail and keeps Dexie rows small enough to sync over
    // the 5MB relay cap.
    const compressed = await compressImage(file, { maxWidth: 400, maxHeight: 400, quality: 0.82 });
    setPhoto(compressed);
  };
  const initialKind: NeedleKind =
    initial?.needleKind ||
    (initial?.notes?.toLowerCase().includes('interchangeable') ? 'interchangeable-tip'
      : initial?.notes?.toLowerCase().includes('circular') ? 'fixed-circular'
      : 'straight');
  const [needleKind, setNeedleKind] = useState<NeedleKind>(initialKind);
  const [cableLength, setCableLength] = useState(
    initial?.cableLength || inferCableLengthFromNotes(initial?.notes) || ''
  );

  const handleSubmit = (e: { preventDefault(): void }) => {
    e.preventDefault();
    if (!name.trim()) return;
    const isNeedle = type === 'needle';
    const isAccessory = type === 'accessory';
    const kind = isNeedle ? needleKind : undefined;
    const noteParts: string[] = [];
    if (kind === 'fixed-circular') noteParts.push(`Circular (fixed${cableLength ? `, cable: ${cableLength}` : ''})`);
    if (kind === 'interchangeable-tip') noteParts.push('Interchangeable');
    const needleNotes = noteParts.length > 0 ? noteParts.join(', ') : undefined;
    const notes = isAccessory ? (description.trim() || undefined) : needleNotes;
    onSubmit({
      type,
      name,
      brand: isAccessory ? (brand.trim() || undefined) : undefined,
      size,
      length,
      material,
      quantity: Math.max(1, parseInt(quantity) || 1),
      notes,
      needleKind: kind,
      cableLength: kind === 'fixed-circular' ? cableLength : undefined,
      photo: isAccessory ? photo : undefined,
    });
  };

  // 2026-05-10 build 1261 — InventoryForm B1 zine port. Veronika
  // sent the Edit Needle modal screenshot and asked to redesign;
  // the form was the last surviving pre-zine surface inside the
  // /inventory page. Same vocabulary as the AddYarnModal /
  // YarnDetailModal: mono-caps labels, flat ink-bordered cream
  // inputs, radio tiles with ink dot when selected, and a black
  // primary EDIT-style button + outlined CLOSE-style cancel +
  // outlined-red trash for delete.
  const monoFamily = 'var(--font-mono)';
  const sansFamily = 'var(--font-geist-sans), system-ui, sans-serif';
  const labelStyle: CSSProperties = {
    display: 'block',
    fontFamily: monoFamily,
    fontSize: 9.5,
    fontWeight: 700,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: 'var(--ink-2)',
    marginBottom: 6,
  };
  const inputStyle: CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    background: 'var(--paper-2)',
    border: '1px solid var(--ink)',
    fontFamily: sansFamily,
    fontSize: 14,
    color: 'var(--ink)',
    outline: 'none',
    borderRadius: 0,
    WebkitAppearance: 'none' as CSSProperties['WebkitAppearance'],
  };
  const radioTileStyle = (active: boolean): CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    background: 'var(--paper-2)',
    border: '1px solid var(--ink)',
    borderLeft: active ? '4px solid var(--ink)' : '1px solid var(--ink)',
    cursor: 'pointer',
    fontFamily: sansFamily,
    fontSize: 13,
    fontWeight: active ? 700 : 500,
    color: 'var(--ink)',
  });
  const radioMarkStyle = (active: boolean): CSSProperties => ({
    width: 14,
    height: 14,
    borderRadius: '50%',
    border: '1px solid var(--ink)',
    background: active ? 'var(--ink)' : 'transparent',
    flexShrink: 0,
    boxShadow: active ? 'inset 0 0 0 2px #F5EFDC' : 'none',
  });
  const photoChooseLabelStyle: CSSProperties = {
    fontFamily: monoFamily,
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: 'var(--ink)',
    background: 'var(--paper-2)',
    border: '1px dashed var(--ink)',
    padding: '8px 12px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
  };
  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }} autoComplete="off">
      {type === 'accessory' ? (
        <>
          <div>
            <label style={labelStyle}>{t.photo}</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {photo ? (
                <BlobImage src={photo} alt="" style={{ width: 64, height: 64, objectFit: 'cover', border: '1px solid var(--ink)' }} />
              ) : (
                <Card tone="paper-1" style={{ width: 64, height: 64 }}>{null}</Card>
              )}
              <label style={photoChooseLabelStyle}>
                {photo ? t.change : t.choose}
                <input type="file" accept="image/*" onChange={handlePhoto} style={{ display: 'none' }} />
              </label>
              {photo && (
                <button
                  type="button"
                  onClick={() => setPhoto(undefined)}
                  style={{
                    fontFamily: monoFamily,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: 'var(--over-num)',
                    background: 'transparent',
                    border: 'none',
                    padding: '8px 4px',
                    cursor: 'pointer',
                  }}
                >
                  {t.removeColorPhoto}
                </button>
              )}
            </div>
          </div>
          <div>
            <label style={labelStyle}>{t.accessoryNameLabel}</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} required placeholder={t.accessoryNamePlaceholder} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>{t.brand}</label>
            <input type="text" value={brand} onChange={e => setBrand(e.target.value)} placeholder={t.brand} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>{t.shortDescription}</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder={t.accessoryDescriptionPlaceholder} style={{ ...inputStyle, resize: 'none', fontFamily: sansFamily }} />
          </div>
        </>
      ) : (
        <div>
          <label style={labelStyle}>{t.brand}</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)} required placeholder={type === 'needle' ? 'e.g. KnitPro, Addi, ChiaoGoo' : t.brand} style={inputStyle} />
        </div>
      )}
      {(type === 'needle' || type === 'hook') && (
        <>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{t.size}</label>
              <input type="text" value={size} onChange={e => setSize(e.target.value)} placeholder="e.g. 4.0mm / US 6" style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{type === 'hook' ? t.hookLength : t.needleLength}</label>
              <input type="text" value={length} onChange={e => setLength(e.target.value)} placeholder="e.g. 13cm / 32in" style={inputStyle} />
            </div>
          </div>

          {type === 'needle' && (
            <>
              <div>
                <label style={labelStyle}>{t.needleKindLabel}</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {([
                    { k: 'straight' as NeedleKind, label: t.needleKindStraight },
                    { k: 'fixed-circular' as NeedleKind, label: t.needleKindFixedCircular },
                    { k: 'interchangeable-tip' as NeedleKind, label: t.needleKindInterchangeable },
                  ]).map(({ k, label }) => {
                    const active = needleKind === k;
                    return (
                      <label key={k} style={radioTileStyle(active)}>
                        <input
                          type="radio"
                          name="needleKind"
                          checked={active}
                          onChange={() => setNeedleKind(k)}
                          style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                        />
                        <span aria-hidden style={radioMarkStyle(active)} />
                        <span>{label}</span>
                      </label>
                    );
                  })}
                </div>
                <ItalicBody style={{ marginTop: 6 }}>{t.needleKindHint}</ItalicBody>
              </div>

              {needleKind === 'fixed-circular' && (
                <div style={{ paddingLeft: 10, borderLeft: '2px solid var(--ink)' }}>
                  <label style={labelStyle}>{t.cableLength}</label>
                  <input type="text" value={cableLength} onChange={e => setCableLength(e.target.value)} placeholder="e.g. 40cm, 60cm, 80cm" style={inputStyle} required />
                </div>
              )}
            </>
          )}
        </>
      )}
      {type === 'cable' && (
        <div>
          <label style={labelStyle}>{t.cableLength}</label>
          <input type="text" value={length} onChange={e => setLength(e.target.value)} placeholder="e.g. 40cm, 60cm, 80cm" style={inputStyle} />
        </div>
      )}
      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>{t.material}</label>
          <input type="text" value={material} onChange={e => setMaterial(e.target.value)} placeholder="e.g. Bamboo" style={inputStyle} />
        </div>
        <div style={{ width: 130 }}>
          <label style={labelStyle}>{t.qty}</label>
          <Card style={{ display: 'flex', alignItems: 'stretch' }}>
            <button
              type="button"
              onClick={() => setQuantity(String(Math.max(1, (parseInt(quantity) || 1) - 1)))}
              aria-label="Decrease quantity"
              style={{ width: 36, background: 'transparent', border: 'none', borderRight: '1px solid var(--ink)', fontFamily: sansFamily, fontWeight: 800, fontSize: 18, color: 'var(--ink)', cursor: 'pointer', padding: 0 }}
            >−</button>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={quantity}
              onChange={e => setQuantity(e.target.value.replace(/[^0-9]/g, ''))}
              onBlur={() => { if (!quantity || parseInt(quantity) < 1) setQuantity('1'); }}
              style={{ flex: 1, minWidth: 0, padding: '8px 4px', background: 'transparent', border: 'none', textAlign: 'center', fontFamily: sansFamily, fontWeight: 800, fontSize: 16, color: 'var(--ink)', outline: 'none' }}
            />
            <button
              type="button"
              onClick={() => setQuantity(String((parseInt(quantity) || 0) + 1))}
              aria-label="Increase quantity"
              style={{ width: 36, background: 'transparent', border: 'none', borderLeft: '1px solid var(--ink)', fontFamily: sansFamily, fontWeight: 800, fontSize: 18, color: 'var(--ink)', cursor: 'pointer', padding: 0 }}
            >+</button>
          </Card>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, paddingTop: 4 }}>
        <ZineButton variant="outlined" type="button" onClick={onCancel}>{t.cancel}</ZineButton>
        <ZineButton type="submit">{initial ? t.save : t.add}</ZineButton>
        {onDelete && initial && (
          <ZineButton
            icon
            variant="danger"
            type="button"
            onClick={async () => {
              const confirmText = t.deleteInventoryConfirm.replace('{name}', initial.name);
              if (!confirm(confirmText)) return;
              await onDelete();
            }}
            aria-label={t.delete}
            title={t.delete}
            style={{ flexShrink: 0 }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              <line x1="10" y1="11" x2="10" y2="17"/>
              <line x1="14" y1="11" x2="14" y2="17"/>
            </svg>
          </ZineButton>
        )}
      </div>
    </form>
  );
}

// Standard metric tip sizes for needle sets. Covers every size shipped
// in a full Knit Pro / Addi / ChiaoGoo set plus the in-between .25
// steps that some premium sets include.
const NEEDLE_SET_SIZES_MM = [
  '2.0', '2.25', '2.5', '2.75', '3.0', '3.25', '3.5', '3.75',
  '4.0', '4.5', '5.0', '5.5', '6.0', '6.5', '7.0', '8.0', '9.0', '10.0', '12.0',
];

// Common cable lengths for interchangeable sets (cm).
const CABLE_LENGTHS_CM = ['40', '60', '80', '100', '120', '150'];

export function NeedleSetForm({
  existingNeedles,
  onSubmit,
  onCancel,
}: {
  existingNeedles: InventoryItem[];
  onSubmit: (
    shared: {
      brand: string;
      setName: string;
      material: string;
      length: string;
      cableLength: string;
      kind: NeedleKind;
    },
    sizes: string[],
    cableLengths: string[],
  ) => Promise<void> | void;
  onCancel: () => void;
}) {
  const { t } = useI18n();
  const [kind, setKind] = useState<NeedleKind>('interchangeable-tip');
  const [brand, setBrand] = useState('');
  const [setName, setSetName] = useState('');
  const [material, setMaterial] = useState('');
  const [length, setLength] = useState('');
  const [cableLengthFixed, setCableLengthFixed] = useState('');
  const [sizes, setSizes] = useState<Set<string>>(new Set());
  const [cables, setCables] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [pickingSet, setPickingSet] = useState(false);

  // Dedup existing needles by (brand + material + length + kind) so the
  // "Same as set" picker offers one entry per real-world set. Now scoped
  // to all kinds, not just interchangeable.
  const existingSets = useMemo(() => {
    const seen = new Map<string, { brand: string; setName: string; material: string; length: string; kind: NeedleKind }>();
    for (const n of existingNeedles) {
      if (!(n.name || '').trim()) continue;
      if (!n.needleKind) continue;
      const key = `${(n.name || '').trim().toLowerCase()}|${(n.setName || '').trim().toLowerCase()}|${(n.material || '').trim().toLowerCase()}|${(n.length || '').trim().toLowerCase()}|${n.needleKind}`;
      if (seen.has(key)) continue;
      seen.set(key, {
        brand: n.name || '',
        setName: n.setName || '',
        material: n.material || '',
        length: n.length || '',
        kind: n.needleKind,
      });
    }
    return Array.from(seen.values());
  }, [existingNeedles]);

  const toggleSize = (size: string) => {
    setSizes(prev => {
      const next = new Set(prev);
      if (next.has(size)) next.delete(size);
      else next.add(size);
      return next;
    });
  };

  const toggleCable = (len: string) => {
    setCables(prev => {
      const next = new Set(prev);
      if (next.has(len)) next.delete(len);
      else next.add(len);
      return next;
    });
  };

  const handleSubmit = async (e: { preventDefault(): void }) => {
    e.preventDefault();
    if (!brand.trim()) { setError(t.brandRequiredError); return; }
    if (sizes.size === 0) { setError(t.sizesRequiredError); return; }
    setError(null);
    const sizeList = Array.from(sizes)
      .sort((a, b) => parseFloat(a) - parseFloat(b))
      .map(s => `${s}mm`);
    const cableList = Array.from(cables)
      .sort((a, b) => parseFloat(a) - parseFloat(b))
      .map(c => `${c}cm`);
    await onSubmit(
      {
        brand: brand.trim(),
        setName: setName.trim(),
        material: material.trim(),
        length: length.trim(),
        cableLength: cableLengthFixed.trim(),
        kind,
      },
      sizeList,
      cableList,
    );
  };

  // 2026-05-19 build 1472 (Veronika msg 4186) — B1 zine port of the
  // needle-set form. Same fields/state/behavior; only the visual recipe
  // matches the rest of the inventory forms (ink-bordered square
  // inputs, mono-caps labels, sans-bold values, square radio tiles,
  // square chip grid for sizes/cables, ZineButton submit row).
  const monoFamily = 'var(--font-mono)';
  const sansFamily = 'var(--font-geist-sans), system-ui, sans-serif';
  const labelStyle: CSSProperties = {
    display: 'block',
    fontFamily: monoFamily,
    fontSize: 9.5,
    fontWeight: 700,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: 'var(--ink-2)',
    marginBottom: 6,
  };
  const inputStyle: CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    background: 'var(--paper-2)',
    border: '1px solid var(--ink)',
    fontFamily: sansFamily,
    fontSize: 14,
    color: 'var(--ink)',
    outline: 'none',
    borderRadius: 0,
    WebkitAppearance: 'none' as CSSProperties['WebkitAppearance'],
  };
  const radioTileStyle = (active: boolean): CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    background: 'var(--paper-2)',
    border: '1px solid var(--ink)',
    borderLeft: active ? '4px solid var(--ink)' : '1px solid var(--ink)',
    cursor: 'pointer',
    fontFamily: sansFamily,
    fontSize: 13,
    fontWeight: active ? 700 : 500,
    color: 'var(--ink)',
  });
  const radioMarkStyle = (active: boolean): CSSProperties => ({
    width: 14,
    height: 14,
    borderRadius: '50%',
    border: '1px solid var(--ink)',
    background: active ? 'var(--ink)' : 'transparent',
    flexShrink: 0,
    boxShadow: active ? 'inset 0 0 0 2px #F5EFDC' : 'none',
  });
  const chipStyle = (active: boolean): CSSProperties => ({
    padding: '10px 0',
    background: active ? 'var(--ink)' : 'var(--paper-2)',
    color: active ? 'var(--paper-1)' : 'var(--ink)',
    border: '1px solid var(--ink)',
    fontFamily: sansFamily,
    fontSize: 13,
    fontWeight: active ? 700 : 500,
    cursor: 'pointer',
    borderRadius: 0,
    textAlign: 'center',
  });
  const dashedToggleStyle: CSSProperties = {
    fontFamily: monoFamily,
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: 'var(--ink)',
    background: 'var(--paper-2)',
    border: '1px dashed var(--ink)',
    padding: '8px 12px',
    cursor: 'pointer',
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }} autoComplete="off">
      <ItalicBody>{t.needleSetHint}</ItalicBody>

      {/* Same-as-set prefill picker */}
      {existingSets.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setPickingSet(v => !v)}
            style={dashedToggleStyle}
          >
            {t.sameAsSetLabel}
          </button>
          {pickingSet && (
            <div style={{ marginTop: 8, maxHeight: 160, overflowY: 'auto', background: 'var(--paper-2)', border: '1px solid var(--ink)', padding: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {existingSets.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => {
                    setKind(s.kind);
                    setBrand(s.brand);
                    setSetName(s.setName);
                    setMaterial(s.material);
                    setLength(s.length);
                    setPickingSet(false);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '6px 8px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: sansFamily,
                    fontSize: 12,
                    color: 'var(--ink)',
                  }}
                >
                  <span style={{ fontWeight: 700 }}>{s.brand}</span>
                  {s.setName && <span style={{ color: 'var(--ink-2)' }}> · {s.setName}</span>}
                  {s.material && <span style={{ color: 'var(--ink-3)' }}> · {s.material}</span>}
                  {s.length && <span style={{ color: 'var(--ink-3)' }}> · {s.length}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Kind picker — a set can be interchangeable tips, a series of
           fixed circulars, or a series of straights. */}
      <div>
        <label style={labelStyle}>{t.needleKindLabel}</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {([
            { k: 'interchangeable-tip' as NeedleKind, label: t.needleKindInterchangeable },
            { k: 'fixed-circular' as NeedleKind, label: t.needleKindFixedCircular },
            { k: 'straight' as NeedleKind, label: t.needleKindStraight },
          ]).map(({ k, label }) => {
            const active = kind === k;
            return (
              <label key={k} style={radioTileStyle(active)}>
                <input
                  type="radio"
                  name="setKind"
                  checked={active}
                  onChange={() => setKind(k)}
                  style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                />
                <span aria-hidden style={radioMarkStyle(active)} />
                <span>{label}</span>
              </label>
            );
          })}
        </div>
      </div>

      <div>
        <label style={labelStyle}>{t.brand}</label>
        <input
          type="text"
          value={brand}
          onChange={e => setBrand(e.target.value)}
          placeholder="e.g. KnitPro, Addi, ChiaoGoo"
          style={inputStyle}
        />
      </div>

      <div>
        <label style={labelStyle}>{t.setNameLabel}</label>
        <input
          type="text"
          value={setName}
          onChange={e => setSetName(e.target.value)}
          placeholder={t.setNamePlaceholder}
          style={inputStyle}
        />
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>{t.material}</label>
          <input
            type="text"
            value={material}
            onChange={e => setMaterial(e.target.value)}
            placeholder="e.g. Bamboo"
            style={inputStyle}
          />
        </div>
        {kind === 'interchangeable-tip' ? (
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>{t.tipLength}</label>
            <input
              type="text"
              value={length}
              onChange={e => setLength(e.target.value)}
              placeholder="e.g. 13cm"
              style={inputStyle}
            />
          </div>
        ) : kind === 'fixed-circular' ? (
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>{t.cableLength}</label>
            <input
              type="text"
              value={cableLengthFixed}
              onChange={e => setCableLengthFixed(e.target.value)}
              placeholder="e.g. 80cm"
              style={inputStyle}
            />
          </div>
        ) : (
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>{t.needleLength}</label>
            <input
              type="text"
              value={length}
              onChange={e => setLength(e.target.value)}
              placeholder="e.g. 30cm"
              style={inputStyle}
            />
          </div>
        )}
      </div>

      <div>
        <label style={labelStyle}>{t.selectSizes}</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
          {NEEDLE_SET_SIZES_MM.map(size => {
            const active = sizes.has(size);
            return (
              <button
                key={size}
                type="button"
                onClick={() => toggleSize(size)}
                style={chipStyle(active)}
              >
                {size}mm
              </button>
            );
          })}
        </div>
      </div>

      {/* Cables section — only for interchangeable sets. Each ticked
           length spawns one cable inventory row so they show up in the
           Cables tab and can be picked from project forms. */}
      {kind === 'interchangeable-tip' && (
        <div>
          <label style={labelStyle}>{t.setCablesLabel}</label>
          <ItalicBody style={{ marginBottom: 6 }}>{t.setCablesHint}</ItalicBody>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
            {CABLE_LENGTHS_CM.map(len => {
              const active = cables.has(len);
              return (
                <button
                  key={len}
                  type="button"
                  onClick={() => toggleCable(len)}
                  style={chipStyle(active)}
                >
                  {len}cm
                </button>
              );
            })}
          </div>
        </div>
      )}

      {error && (
        <p style={{ fontFamily: sansFamily, fontSize: 12, fontWeight: 600, color: 'var(--over-num)', margin: 0 }}>{error}</p>
      )}

      <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
        <ZineButton variant="outlined" fullWidth onClick={onCancel}>{t.cancel}</ZineButton>
        <ZineButton fullWidth type="submit">{t.add}</ZineButton>
      </div>
    </form>
  );
}
